import { useDispatch, useSelector } from 'react-redux';
import { translate } from '../../lib/lang.helper';
import {
  Button,
  CheckboxButton,
  CloseIcon,
  Dropdown,
  Input,
  Subtitle,
} from '../shared';
import ModalFeedbackView from '../shared/feedback/ModalFeedbackView';
import { ITag } from '../../entities/automation.entities';
import { selectLang } from '../../store/ui/ui.selector';
import { selectFarmsData } from '../../store/farms/farms.selector';
import { useState } from 'react';
import {
  calcPeriodUnit,
  isColorFormat,
  numToStr,
} from '../../lib/common.helpers';
import { sendSingleRequest } from '../../apis';
import { loadTags } from '../../store/automation/automation.actions';
import { showFeedback } from '../../store/ui/ui.actions';
import { Modal } from 'antd';

interface IForm {
  name: string;
  color: string;
  lines?: number[];
  is_urgent: boolean;
}

const periodOptions = [
  { id: 'm', value: 'month', label: 'Month(s)' },
  { id: 'w', value: 'week', label: 'Week(s)' },
  { id: 'd', value: 'day', label: 'Day(s)' },
  { id: 'h', value: 'hour', label: 'Hour(s)' },
];

interface Props {
  tag?: ITag;
  onClose: () => void;
  editId?: number;
}

const TagDataModal = ({ tag, onClose, editId }: Props) => {
  const dispatch = useDispatch<any>();
  const lang = useSelector(selectLang);

  const linesOption = useSelector(selectFarmsData).reduce(
    (p, c) => [
      ...p,
      ...c.lines.map(x => ({
        id: x.id,
        value: x.id.toString(),
        label: `${c.name} - ${x.line_name}`,
      })),
    ],
    [] as any,
  );
  const [disabled, setDisabled] = useState(false);
  const [form, setForm] = useState<IForm>({
    name: tag?.name ?? '',
    color: tag?.color ?? '',
    lines: tag?.tag_lines.map(x => x.line_id) ?? [],
    is_urgent: tag?.is_urgent ?? false,
  });
  const [periodUnit, setPeriodUnit] = useState(
    tag?.period ? calcPeriodUnit(tag.period) : { unit: 'day', value: null },
  );

  const updateForm = (key: keyof IForm, value: any) => {
    setForm({ ...form, [key]: value });
  };
  const confirmClick = async () => {
    if (!form.name) {
      window.alert(translate(lang, '_enter_tag_name'));
      return;
    }
    if (form.color && !isColorFormat(form.color)) {
      window.alert(translate(lang, '_enter_valid_color'));
      return;
    }
    let data: any = { ...form };
    if (form.is_urgent) {
      if (
        !periodUnit ||
        !periodUnit.unit ||
        periodUnit.value === undefined ||
        periodUnit.value === null
      ) {
        window.alert(translate(lang, '_enter_valid_period_unit'));
        return;
      }
      const period =
        periodUnit.unit === 'month'
          ? periodUnit.value * 3600 * 24 * 30
          : periodUnit.unit === 'week'
            ? periodUnit.value * 3600 * 24 * 7
            : periodUnit.unit === 'day'
              ? periodUnit.value * 3600 * 24
              : periodUnit.value * 3600;
      data.period = period;
    }
    setDisabled(true);
    const res = editId
      ? await sendSingleRequest(data, 'PUT', `api/tag/${editId}`, true)
      : await sendSingleRequest(data, 'POST', 'api/tag', true);

    if (res.status) {
      await dispatch(loadTags());
      onClose();
    } else {
      setDisabled(false);
      dispatch(
        showFeedback({
          isMessageModal: true,
          message: translate(lang, res.data?.message ?? 'Unknown error'),
          type: 'error',
        }),
      );
    }
  };

  return (
    <Modal
      visible={true}
      onCancel={onClose}
      footer={null}
      closable
      closeIcon={<CloseIcon />}
    >
      <div className='wrap'>
        <div className='d-flex align-center mb-32'>
          <Subtitle color='black-1' align='left' size={1} fontWeight={600}>
            {translate(lang, `${editId ? 'Update Tag' : 'Add New Tag'}`)}
          </Subtitle>
        </div>
        <Input
          className='mr-16 w-100 mb-17'
          type='text'
          label={translate(lang, 'Name')}
          value={form.name}
          onChange={e => updateForm('name', e.target.value)}
          disabled={disabled}
        />
        <div className='mb-17'>
          <Subtitle color='black-1' align='left' size={4} fontWeight={500}>
            {translate(lang, 'Color')}
          </Subtitle>
          <div className='d-flex align-center gap-10'>
            <input
              type='color'
              value={form.color || '#000000'}
              onChange={e => updateForm('color', e.target.value)}
              disabled={disabled}
              className='input-color'
            />
            <Input
              className='w-100'
              type='text'
              label=''
              placeholder='#000000'
              value={form.color ?? ''}
              onChange={e => updateForm('color', e.target.value)}
              disabled={disabled}
            />
          </div>
        </div>
        <div className='mb-17'>
          <Dropdown
            label={translate(lang, 'Tagged lines')}
            placeholder={translate(lang, 'Select tagged lines')}
            mode='multiple'
            options={linesOption}
            value={form.lines?.map(x => x.toString()) as any}
            onChange={(v: any) =>
              updateForm(
                'lines',
                v.map((x: string) => Number(x)),
              )
            }
            showSearch={true}
            disabled={disabled}
          />
        </div>
        <div className='mt-32 mb-17'>
          <CheckboxButton
            label={translate(lang, '_check_tag_requires_urgent')}
            checked={form.is_urgent}
            onChange={e => updateForm('is_urgent', e.target.checked)}
            disabled={disabled}
          />
        </div>
        {form.is_urgent && (
          <>
            <div className='mb-7'>
              <Subtitle color='black-1' align='left' size={4} fontWeight={500}>
                {translate(lang, '_long_delayed')}
              </Subtitle>
            </div>
            <div className='mb-17 d-flex'>
              <div className='w-100 mr-7'>
                <Input
                  type='number'
                  label=''
                  value={numToStr(periodUnit.value)}
                  onChange={e =>
                    setPeriodUnit((prv: any) => ({
                      ...prv,
                      value:
                        e.target.value.length <= 0
                          ? null
                          : Number(e.target.value),
                    }))
                  }
                />
              </div>
              <div className='w-100 ml-7'>
                <Dropdown
                  label=''
                  options={periodOptions}
                  value={periodUnit.unit}
                  onChange={v =>
                    setPeriodUnit((prv: any) => ({ ...prv, unit: v }))
                  }
                />
              </div>
            </div>
          </>
        )}
      </div>
      <div className='modal-button d-flex justify-end align-center'>
        <Button
          width={'small'}
          size={2}
          type='fill'
          color='green'
          className='rsp-btn ml-16 mt-24'
          onClick={confirmClick}
          disabled={disabled}
        >
          {translate(lang, 'Confirm')}
        </Button>
      </div>
      <ModalFeedbackView />
    </Modal>
  );
};

export default TagDataModal;
